import { useEffect, useState } from 'react';

import {
  LoadingOutlined,
  MinusCircleFilled,
  PlusCircleFilled,
  SearchOutlined,
  ShopOutlined,
} from '@ant-design/icons';
import {
  App,
  Button,
  Card,
  Empty,
  Skeleton,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import i18n from 'i18next';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import EvidenceModal from 'scenes/Kit/DashboardAlternativeMode/components/EvidenceModal';
import { useEvidenceModal } from 'scenes/Kit/DashboardAlternativeMode/hooks/useEvidenceModal';

import { ApiErrorData } from 'models/ApiResponse';

import { KitCampaignService } from 'services/KitCampaignService';

import {
  TurnDetailRowForPortalTouchKitRow,
  TurnSummaryStatusGridData,
} from '../types';
import { getComplianceColor } from '../utils';

const BASE = 'scenes.kit.dashboard.components.dashboard_table';

const { Text } = Typography;

// ── Colores de estado del turno (FINISHED / IN_PROGRESS / PENDING) ────────────

type TurnStatusKey = 'FINISHED' | 'IN_PROGRESS' | 'PENDING';

const TURN_STATUS_COLORS: Record<TurnStatusKey, { bg: string; text: string }> =
  {
    FINISHED: { bg: '#d9f7be', text: '#389e0d' },
    IN_PROGRESS: { bg: '#e6f4ff', text: '#1677ff' },
    PENDING: { bg: '#f0f0f0', text: '#8c8c8c' },
  };

const TURN_STATUS_FALLBACK: { bg: string; text: string } = {
  bg: '#f0f0f0',
  text: '#8c8c8c',
};

// ── Tipos de fila aplanada ──────────────────────────────────────────────────

interface DashboardTableProps {
  data?: TurnSummaryStatusGridData[];
  loading?: boolean;
  dateRange?: [Dayjs, Dayjs];
  filterKey?: number;
  touchLatamApiUrl: string;
}

interface CampaignFlatRow {
  _type: 'campaign';
  key: string;
  taskId: number;
  taskName: string;
  totalShifts: number;
  executedShifts: number;
  pendingShifts: number;
  compliancePct: number;
}

interface StoreFlatRow {
  _type: 'store';
  key: string;
  taskId: number;
  storeId: number;
  storeName: string;
  totalShifts: number;
  executedShifts: number;
  pendingShifts: number;
  compliancePct: number;
}

interface StoreDetailFlatRow {
  _type: 'store_detail';
  key: string;
  taskId: number;
  storeId: number;
}

type FlatRow = CampaignFlatRow | StoreFlatRow | StoreDetailFlatRow;

// ── Helpers: agrupación ───────────────────────────────────────────────────────

interface GroupedCampaign {
  taskId: number;
  taskName: string;
  totalShifts: number;
  executedShifts: number;
  pendingShifts: number;
  compliancePct: number;
  stores: TurnSummaryStatusGridData[];
}

function buildGroups(data: TurnSummaryStatusGridData[]): GroupedCampaign[] {
  const map: Record<string, GroupedCampaign> = {};
  for (const row of data) {
    const k = String(row.taskId);
    if (!map[k]) {
      map[k] = {
        taskId: row.taskId,
        taskName: row.taskName,
        totalShifts: 0,
        executedShifts: 0,
        pendingShifts: 0,
        compliancePct: 0,
        stores: [],
      };
    }
    map[k].totalShifts += row.totalShifts;
    map[k].executedShifts += row.executedShifts;
    map[k].pendingShifts += row.pendingShifts;
    map[k].stores.push(row);
  }
  return Object.values(map).map((g) => {
    const n = g.stores.length;
    g.compliancePct =
      n > 0 ? g.stores.reduce((s, r) => s + r.compliancePct, 0) / n : 0;
    return g;
  });
}

// Clave única de store expandida
function storeKey(taskId: number, storeId: number): string {
  return `${taskId}-${storeId}`;
}

function buildFlatRows(
  groups: GroupedCampaign[],
  expanded: Set<number>,
  expandedStores: Set<string>
): FlatRow[] {
  const rows: FlatRow[] = [];
  for (const g of groups) {
    rows.push({
      _type: 'campaign',
      key: `campaign-${g.taskId}`,
      taskId: g.taskId,
      taskName: g.taskName,
      totalShifts: g.totalShifts,
      executedShifts: g.executedShifts,
      pendingShifts: g.pendingShifts,
      compliancePct: g.compliancePct,
    });
    if (expanded.has(g.taskId)) {
      for (const s of g.stores) {
        rows.push({
          _type: 'store',
          key: `store-${g.taskId}-${s.storeId}`,
          taskId: g.taskId,
          storeId: s.storeId,
          storeName: s.storeName,
          totalShifts: s.totalShifts,
          executedShifts: s.executedShifts,
          pendingShifts: s.pendingShifts,
          compliancePct: s.compliancePct,
        });
        // Si esta store está expandida, insertar fila detalle justo después
        if (expandedStores.has(storeKey(g.taskId, s.storeId))) {
          rows.push({
            _type: 'store_detail',
            key: `store_detail-${g.taskId}-${s.storeId}`,
            taskId: g.taskId,
            storeId: s.storeId,
          });
        }
      }
    }
  }
  return rows;
}

// ── Sub-tabla de detalle de turnos ────────────────────────────────────────────

interface StoreDetailSubTableProps {
  rows: TurnDetailRowForPortalTouchKitRow[];
  loading: boolean;
  onOpenDetail: (userId: number, turnId: number) => void;
}

function StoreDetailSubTable({
  rows,
  loading,
  onOpenDetail,
}: StoreDetailSubTableProps) {
  const { t } = useTranslation();
  const dateFormat: string = useSelector((state: any) => state.intl.dateFormat);

  const SUB_BASE = `${BASE}.detail_table`;

  const columns: ColumnsType<TurnDetailRowForPortalTouchKitRow> = [
    {
      title: t(`${SUB_BASE}.columns.turn_date`),
      key: 'turnDate',
      width: '30%',
      onHeaderCell: () => ({ style: { paddingLeft: 56 } }),
      onCell: () => ({ style: { paddingLeft: 56 } }),
      render: (_: unknown, row: TurnDetailRowForPortalTouchKitRow) => (
        <Text>{dayjs(row.turnDate).format(dateFormat)}</Text>
      ),
    },
    {
      title: t(`${SUB_BASE}.columns.capturer`),
      key: 'taskerName',
      width: '30%',
      render: (_: unknown, row: TurnDetailRowForPortalTouchKitRow) => (
        <Text>{row.taskerName}</Text>
      ),
    },
    {
      title: t(`${SUB_BASE}.columns.turn_status`),
      key: 'turnStatusId',
      width: '25%',
      align: 'center',
      render: (_: unknown, row: TurnDetailRowForPortalTouchKitRow) => {
        const statusKey = row.turnStatusId as TurnStatusKey;
        const colors = TURN_STATUS_COLORS[statusKey] ?? TURN_STATUS_FALLBACK;
        return (
          <Tag
            style={{
              backgroundColor: colors.bg,
              color: colors.text,
              borderColor: colors.bg,
              fontWeight: 500,
              margin: 0,
            }}
          >
            {t(`${SUB_BASE}.turn_status.${statusKey}`, {
              defaultValue: statusKey,
            })}
          </Tag>
        );
      },
    },
    {
      title: t(`${SUB_BASE}.columns.detail`),
      key: 'detail',
      width: '15%',
      align: 'center',
      render: (_: unknown, row: TurnDetailRowForPortalTouchKitRow) => (
        <Button
          type="text"
          icon={<SearchOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            onOpenDetail(row.userId, row.turnId);
          }}
        />
      ),
    },
  ];

  if (loading) {
    return (
      <div
        style={{
          margin: '0 16px 4px 16px',
          border: '1px solid #e8edf2',
          borderRadius: 6,
          overflow: 'hidden',
          background: '#fff',
          padding: '10px 16px 10px 72px',
        }}
      >
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              display: 'grid',
              gridTemplateColumns: '30% 30% 25% 15%',
              gap: 8,
              padding: '8px 0',
              borderBottom: i < 2 ? '1px solid #f0f0f0' : 'none',
            }}
          >
            <Skeleton.Input active size="small" style={{ width: '90%' }} />
            <Skeleton.Input active size="small" style={{ width: '80%' }} />
            <Skeleton.Input active size="small" style={{ width: '70%' }} />
            <Skeleton.Button active size="small" style={{ width: '100%' }} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      style={{
        margin: '0 16px 4px 16px',
        border: '1px solid #e8edf2',
        borderRadius: 6,
        overflow: 'hidden',
        background: '#fff',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        paddingTop: 12,
        paddingBottom: 12,
      }}
    >
      <Table<TurnDetailRowForPortalTouchKitRow>
        columns={columns}
        dataSource={rows}
        rowKey={(row) => `${row.userId}-${row.turnId}`}
        pagination={false}
        size="small"
        showHeader={rows.length > 0}
        locale={{
          emptyText: (
            <Empty
              description={
                <span style={{ color: '#d9d9d9' }}>
                  {t(`${SUB_BASE}.empty`)}
                </span>
              }
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ),
        }}
      />
    </div>
  );
}

// ── ComplianceText ────────────────────────────────────────────────────────────

function ComplianceText({ value }: { value: number }) {
  return (
    <Text strong style={{ color: getComplianceColor(value) }}>
      {value.toFixed(2)}%
    </Text>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────

function DashboardTable({
  data,
  loading,
  dateRange,
  filterKey,
  touchLatamApiUrl,
}: DashboardTableProps) {
  const { t } = useTranslation();
  const { message } = App.useApp();

  // Nivel 1→2: campañas expandidas
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  // Nivel 2→3: stores expandidas (múltiple, libre)
  const [expandedStores, setExpandedStores] = useState<Set<string>>(new Set());

  // Cache de datos del tercer nivel por clave "taskId-storeId"
  const [storeDetails, setStoreDetails] = useState<
    Record<string, TurnDetailRowForPortalTouchKitRow[]>
  >({});
  const [storeDetailLoading, setStoreDetailLoading] = useState<
    Record<string, boolean>
  >({});

  const evidenceModal = useEvidenceModal({ message });

  // Resetear expansiones y caché al aplicar un nuevo filtro.
  // Se usa filterKey (incrementado en cada Apply/Reset) en lugar de `data`
  // para evitar el problema de comparación por referencia de arrays.
  useEffect(() => {
    setExpanded(new Set());
    setExpandedStores(new Set());
    setStoreDetails({});
    setStoreDetailLoading({});
  }, [filterKey]);

  const groups = buildGroups(data ?? []);
  const flatRows = buildFlatRows(groups, expanded, expandedStores);

  // Toggle campaña (nivel 1→2). Al colapsar, también limpia las stores hijas.
  const toggle = (taskId: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
        // Limpiar stores expandidas que pertenecen a esta campaña
        setExpandedStores((prevStores) => {
          const nextStores = new Set(prevStores);
          for (const k of nextStores) {
            if (k.startsWith(`${taskId}-`)) nextStores.delete(k);
          }
          return nextStores;
        });
      } else {
        next.add(taskId);
      }
      return next;
    });
  };

  // Toggle store (nivel 2→3): expansión libre, recuerda datos ya cargados
  const toggleStoreDetail = async (taskId: number, storeId: number) => {
    const key = storeKey(taskId, storeId);

    // Si ya está expandida → colapsar (recordando la caché)
    if (expandedStores.has(key)) {
      setExpandedStores((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      return;
    }

    // Expandir
    setExpandedStores((prev) => new Set(prev).add(key));

    // Si ya hay datos en caché, no volver a pedir
    if (storeDetails[key] !== undefined) return;

    setStoreDetailLoading((prev) => ({ ...prev, [key]: true }));

    const response =
      await KitCampaignService.getTurnDetailRowsByTaskAndStoreForPortalTouchKit(
        {
          taskId,
          storeId,
          startDate: dateRange
            ? dateRange[0].format('YYYY-MM-DD')
            : dayjs().subtract(14, 'day').format('YYYY-MM-DD'),
          endDate: dateRange
            ? dateRange[1].format('YYYY-MM-DD')
            : dayjs().format('YYYY-MM-DD'),
        }
      );

    if (response.success) {
      setStoreDetails((prev) => ({
        ...prev,
        [key]: response.data as TurnDetailRowForPortalTouchKitRow[],
      }));
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          i18n.t(`${BASE}.detail_table.errors.fetch_failed`),
        3.5
      );
      // Colapsar si falla la carga
      setExpandedStores((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }

    setStoreDetailLoading((prev) => ({ ...prev, [key]: false }));
  };

  const columns: ColumnsType<FlatRow> = [
    {
      title: t(`${BASE}.table.columns.campaign_store`),
      key: 'label',
      width: '30%',
      onCell: (row) =>
        row._type === 'store_detail'
          ? {
              colSpan: 5,
              style: { padding: '8px 0 12px 0', background: '#f8fafc' },
            }
          : {},
      render: (_: unknown, row: FlatRow) => {
        if (row._type === 'campaign') {
          const isOpen = expanded.has(row.taskId);
          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              {isOpen ? (
                <MinusCircleFilled style={{ fontSize: 16, color: '#00BFB3' }} />
              ) : (
                <PlusCircleFilled style={{ fontSize: 16, color: '#00BFB3' }} />
              )}
              <Text strong style={{ color: '#003087', marginLeft: 20 }}>
                {row.taskName}
              </Text>
            </div>
          );
        }

        if (row._type === 'store') {
          const key = storeKey(row.taskId, row.storeId);
          const isStoreOpen = expandedStores.has(key);
          const isLoadingDetail = !!storeDetailLoading[key];

          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                paddingLeft: 44,
              }}
            >
              {isLoadingDetail ? (
                <LoadingOutlined
                  style={{ flexShrink: 0, fontSize: 15, color: '#595959' }}
                />
              ) : isStoreOpen ? (
                <ChevronUpIcon
                  size={15}
                  style={{ flexShrink: 0, color: '#595959' }}
                />
              ) : (
                <ChevronDownIcon
                  size={15}
                  style={{ flexShrink: 0, color: '#595959' }}
                />
              )}
              <ShopOutlined style={{ color: '#595959', flexShrink: 0 }} />
              <Text strong>{row.storeName}</Text>
            </div>
          );
        }

        // store_detail: ocupa todas las columnas via colSpan
        if (row._type === 'store_detail') {
          const key = storeKey(row.taskId, row.storeId);
          return (
            <StoreDetailSubTable
              rows={storeDetails[key] ?? []}
              loading={!!storeDetailLoading[key]}
              onOpenDetail={evidenceModal.openModal}
            />
          );
        }

        return null;
      },
    },
    {
      title: t(`${BASE}.table.columns.required`),
      key: 'totalShifts',
      align: 'center',
      width: '17%',
      onCell: (row) => (row._type === 'store_detail' ? { colSpan: 0 } : {}),
      render: (_: unknown, row: FlatRow) => {
        if (row._type === 'store_detail') return null;
        return <Text strong>{row.totalShifts}</Text>;
      },
    },
    {
      title: t(`${BASE}.table.columns.executed`),
      key: 'executedShifts',
      align: 'center',
      width: '17%',
      onCell: (row) => (row._type === 'store_detail' ? { colSpan: 0 } : {}),
      render: (_: unknown, row: FlatRow) => {
        if (row._type === 'store_detail') return null;
        return <Text strong>{row.executedShifts}</Text>;
      },
    },
    {
      title: t(`${BASE}.table.columns.pending`),
      key: 'pendingShifts',
      align: 'center',
      width: '17%',
      onCell: (row) => (row._type === 'store_detail' ? { colSpan: 0 } : {}),
      render: (_: unknown, row: FlatRow) => {
        if (row._type === 'store_detail') return null;
        return <Text strong>{row.pendingShifts}</Text>;
      },
    },
    {
      title: t(`${BASE}.table.columns.compliance`),
      key: 'compliancePct',
      align: 'center',
      width: '17%',
      onCell: (row) => (row._type === 'store_detail' ? { colSpan: 0 } : {}),
      render: (_: unknown, row: FlatRow) => {
        if (row._type === 'store_detail') return null;
        return <ComplianceText value={row.compliancePct} />;
      },
    },
  ];

  return (
    <>
      <Card title={t(`${BASE}.title`)}>
        <Table<FlatRow>
          className="kit-dashboard__ops-table"
          columns={columns}
          dataSource={flatRows}
          rowKey="key"
          loading={loading}
          pagination={false}
          locale={{
            emptyText: (
              <Empty
                description={
                  <span style={{ color: '#d9d9d9' }}>{t(`${BASE}.empty`)}</span>
                }
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
          size="middle"
          onRow={(row) => ({
            onClick: () => {
              if (row._type === 'campaign') toggle(row.taskId);
              if (row._type === 'store')
                toggleStoreDetail(row.taskId, row.storeId);
            },
            style: {
              cursor:
                row._type === 'campaign' || row._type === 'store'
                  ? 'pointer'
                  : 'default',
            },
          })}
          rowClassName={(row) => {
            if (row._type === 'store') return 'kit-dashboard__store-row';
            if (row._type === 'store_detail')
              return 'kit-dashboard__store-detail-row';
            return '';
          }}
        />
      </Card>

      <EvidenceModal
        open={evidenceModal.open}
        loading={evidenceModal.loading}
        detail={evidenceModal.detail}
        onClose={evidenceModal.closeModal}
        touchLatamApiUrl={touchLatamApiUrl}
      />
    </>
  );
}

export default DashboardTable;
